import React, { useEffect, useRef, useState } from "react";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import { Upload, FileSpreadsheet, ShieldCheck, Wallet, CheckCircle2, Loader2, AlertCircle, Cloud, RefreshCw, History, Download } from "lucide-react";
import api, { formatError } from "../lib/api";

// Poll a Field Assist status endpoint until its background sync finishes (running:false).
async function pollFaStatus(statusPath, onTick) {
  for (let i = 0; i < 200; i++) { // up to ~10 min
    await new Promise((r) => setTimeout(r, 3000));
    try {
      const { data } = await api.get(statusPath);
      if (onTick) onTick(data);
      if (!data.running) return data;
    } catch { /* keep polling */ }
  }
  return null;
}

const SECTIONS = [
  {
    id: "physio-master",
    title: "Physio Master Upload",
    subtitle: "Outlet Dump GeoHierarchy (.xlsx) — creates/updates CBC physio members and L1–L6 hierarchy users.",
    endpoint: "/admin/imports/physio-master",
    icon: FileSpreadsheet,
    accent: "navy",
    expected: ["Outlet Erp Id", "Outlets Name", "Owners Name", "Owners Number", "L1 Position User Erp Id", "L2 Position User Erp Id", "L5 Position User Erp Id"],
  },
  {
    id: "carry-forward",
    title: "Carry Forward Points",
    subtitle: "Imports each physio's opening points balance keyed on Outlet Erp Id.",
    endpoint: "/admin/imports/carry-forward",
    icon: Wallet,
    accent: "teal",
    expected: ["Outlets Erp Id", "Carry forward Opening Points"],
  },
  {
    id: "certified",
    title: "Already Certified Physios",
    subtitle: "Marks Certified=Yes and credits Carry-Forward → starting Active Points for the current quarter.",
    endpoint: "/admin/imports/certified",
    icon: ShieldCheck,
    accent: "emerald",
    expected: ["Outlets Erp Id", "CBC Certificate"],
  },
  {
    id: "legacy-points",
    title: "Legacy / Old Points Upload",
    subtitle: "Ingest historical points earned BEFORE the Field Assist cutover (1 Apr 2026) so they show in the Q1/Q2/Annual columns. Accepts .xlsx or .csv. Use EITHER a single 'Opening Points' total OR a per-quarter breakdown. Stored as active (redeemable) & idempotent (re-upload replaces).",
    endpoint: "/admin/imports/legacy-points",
    icon: History,
    accent: "teal",
    expected: ["Outlets Erp Id", "Opening Points", "Q1 Points", "Q2 Points", "Q3 Points", "Q4 Points"],
    accept: ".xlsx,.xls,.csv",
    template: ["Outlets Erp Id", "Opening Points", "Q1 Points", "Q2 Points", "Q3 Points", "Q4 Points"],
  },
];

function ImportCard({ section }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  async function upload(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setError(""); setResult(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(section.endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function downloadTemplate() {
    const blob = new Blob([section.template.join(",") + "\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${section.id}-template.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Card>
      <div className="flex items-start gap-4">
        <div className={`h-11 w-11 rounded-md grid place-items-center bg-${section.accent === "navy" ? "brand-navy" : section.accent === "teal" ? "teal-600" : "emerald-600"} text-white shrink-0`}>
          <section.icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-medium text-slate-900">{section.title}</h3>
          <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">{section.subtitle}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            <span className="small-caps text-slate-400">Expected columns:</span>
            {section.expected.map((c) => (
              <code key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">{c}</code>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <input type="file" accept={section.accept || ".xlsx,.xls"} ref={inputRef} onChange={upload} className="hidden" data-testid={`import-input-${section.id}`} />
            <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid={`import-btn-${section.id}`}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {busy ? "Importing…" : (section.accept?.includes("csv") ? "Choose file (.xlsx / .csv)" : "Choose Excel file")}
            </button>
            {section.template && (
              <button onClick={downloadTemplate} className="text-[12px] text-teal-700 hover:underline inline-flex items-center gap-1" data-testid={`import-template-${section.id}`}>
                <Download size={13} /> Download template
              </button>
            )}
            {result && (
              <span className="inline-flex items-center gap-1.5 text-[12px] text-emerald-700">
                <CheckCircle2 size={14} /> Done
              </span>
            )}
          </div>

          {error && (
            <div className="mt-3 tag tag-rose flex items-start gap-2"><AlertCircle size={13} className="mt-0.5" /> <span data-testid={`import-error-${section.id}`}>{error}</span></div>
          )}

          {result && (
            <div className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200" data-testid={`import-result-${section.id}`}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(result).filter(([k]) => k !== "ok" && k !== "default_password_note").map(([k, v]) => (
                  <div key={k} className="text-[12px]">
                    <div className="small-caps text-slate-400">{k.replace(/_/g, " ")}</div>
                    <div className="serif-display text-xl text-slate-900 mt-0.5 numerals">{String(v)}</div>
                  </div>
                ))}
              </div>
              {result.default_password_note && (
                <div className="mt-3 pt-3 border-t border-slate-200 text-[11px] text-slate-600">
                  <Tag tone="amber">Note</Tag> {result.default_password_note}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function FieldAssistSyncCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  async function loadStatus() {
    try { const { data } = await api.get("/integrations/field-assist/status"); setStatus(data); }
    catch (e) { setError(formatError(e)); }
  }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/integrations/field-assist/status");
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled) setError(formatError(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function runSync(opts) {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/integrations/field-assist/sync", opts);
      if (data && (data.started || data.running) && !opts.dry_run) {
        // Background sync — poll status until it completes.
        const final = await pollFaStatus("/integrations/field-assist/status", (s) => setStatus(s));
        const lr = (final && final.last_run) || {};
        setResult({ pages: lr.pages, fetched: lr.fetched, created: lr.created, updated: lr.updated, rejected: lr.rejected, from_cursor: lr.from_cursor, to_cursor: lr.to_cursor });
      } else {
        setResult(data);
      }
      loadStatus();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  }

  return (
    <Card title="Field Assist · Stock & Sales Sync" subtitle="Pull live order & dispatch data from Jenburkt's Field Assist account. Maps ShopErpId → physio.erp_outlet_id; AttendanceId = order ID; SaleValue × points-per-rupee = points; DispatchStatus drives pending/active/rejected.">
      <div className="bg-teal-50/40 border border-teal-200 rounded-md p-4 space-y-3" data-testid="fa-sync-card">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[12px]">
          <Stat label="Configured" value={status ? (status.configured ? "Yes" : "No") : "…"} tone={status?.configured ? "emerald" : "amber"} />
          <Stat label="Last cursor" value={status ? fmt(status.last_synced_id) : "…"} />
          <Stat label="Orders synced" value={status ? fmt(status.totals?.orders_recorded || 0) : "…"} />
          <Stat label="Physios w/ FA" value={status ? fmt(status.totals?.physios_with_fa_orders || 0) : "…"} />
        </div>
        {status?.last_run && (
          <div className="text-[11.5px] text-slate-600 bg-white border border-slate-100 rounded p-2">
            <strong>Last run</strong> · {new Date(status.last_run.at).toLocaleString()} by {status.last_run.started_by} · pages {status.last_run.pages} · fetched {status.last_run.fetched} · created {status.last_run.created} · updated {status.last_run.updated} · rejected {status.last_run.rejected}
            {status.last_run.unknown_outlets ? <span className="text-amber-700"> · ⚠️ {status.last_run.unknown_outlets} unmapped outlets</span> : null}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => runSync({ max_pages: 10 })} disabled={busy} className="btn-teal !py-1.5 inline-flex items-center gap-1.5" data-testid="fa-sync-btn">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Cloud size={13} />}
            {busy ? "Syncing…" : "Sync now"}
          </button>
          <button onClick={() => runSync({ dry_run: true, max_pages: 5 })} disabled={busy} className="btn-secondary !py-1.5 inline-flex items-center gap-1.5" data-testid="fa-dryrun-btn">
            <RefreshCw size={12} /> Dry-run preview
          </button>
          <button onClick={() => { if (window.confirm("Full re-pull from lastSyncedId=0?\n\nThis re-fetches every Field Assist order and RECOMPUTES points on NetValue (used to be SaleValue) for all existing transactions, adjusting wallet balances accordingly. Safe to run more than once (idempotent).")) runSync({ reset: true, max_pages: 500 }); }} disabled={busy} className="text-[11.5px] text-rose-700 hover:underline ml-auto">
            Reset cursor &amp; re-pull
          </button>
        </div>
        {error && <div className="text-[12px] text-rose-700 inline-flex items-center gap-1"><AlertCircle size={12} /> {error}</div>}
        {result && (
          <div className="bg-white border border-emerald-200 rounded p-3 text-[11.5px]" data-testid="fa-sync-result">
            <div className="text-emerald-700 font-medium mb-1.5 inline-flex items-center gap-1"><CheckCircle2 size={12} /> {result.dry_run ? "Dry-run complete" : "Sync complete"}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-slate-700 font-mono">
              <div>pages: <strong>{result.pages}</strong></div>
              <div>fetched: <strong>{result.fetched}</strong></div>
              <div>created: <strong>{result.created}</strong></div>
              <div>updated: <strong>{result.updated}</strong></div>
              <div>rejected: <strong>{result.rejected}</strong></div>
              <div>skipped: <strong>{result.skipped}</strong></div>
              <div>unmapped: <strong className="text-amber-700">{result.unknown_outlets}</strong></div>
              <div>cursor: <strong>{result.from_cursor} → {result.to_cursor}</strong></div>
            </div>
            {result.unknown_outlet_sample?.length > 0 && (
              <div className="mt-2 text-[10.5px] text-amber-700">
                <strong>Unmapped ShopErpIds (need to be in Physio Master first):</strong> {result.unknown_outlet_sample.slice(0, 10).join(", ")}{result.unknown_outlet_sample.length > 10 ? "…" : ""}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function FieldAssistOutletsSyncCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  async function loadStatus() {
    try { const { data } = await api.get("/integrations/field-assist/outlets/status"); setStatus(data); }
    catch (e) { setError(formatError(e)); }
  }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/integrations/field-assist/outlets/status");
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled) setError(formatError(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function runSync(opts) {
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/integrations/field-assist/outlets/sync", opts);
      if (data && (data.started || data.running) && !opts.dry_run) {
        const final = await pollFaStatus("/integrations/field-assist/outlets/status", (s) => setStatus(s));
        const lr = (final && final.last_run) || {};
        setResult({ pages: lr.pages, fetched: lr.fetched, created: lr.created, updated: lr.updated, skipped: lr.skipped, from_cursor: lr.from_cursor, to_cursor: lr.to_cursor });
      } else {
        setResult(data);
      }
      loadStatus();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  }

  return (
    <Card title="Field Assist · Outlets → Physio Master" subtitle="Pull the outlet master from Jenburkt's Field Assist account to onboard NEW physios and refresh existing ones. Maps OutletErpId → physio.erp_outlet_id (idempotent). New outlets are auto-approved & active. Runs automatically every day at 6 AM IST.">
      <div className="bg-indigo-50/40 border border-indigo-200 rounded-md p-4 space-y-3" data-testid="fa-outlets-card">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[12px]">
          <Stat label="Configured" value={status ? (status.configured ? "Yes" : "No") : "…"} tone={status?.configured ? "emerald" : "amber"} />
          <Stat label="Last cursor (ShopId)" value={status ? fmt(status.last_request_id) : "…"} />
          <Stat label="Physios from FA" value={status ? fmt(status.totals?.physios_from_fa_outlets || 0) : "…"} />
          <Stat label="Total physios" value={status ? fmt(status.totals?.total_physios || 0) : "…"} />
        </div>
        {status?.last_run && (
          <div className="text-[11.5px] text-slate-600 bg-white border border-slate-100 rounded p-2">
            <strong>Last run</strong> · {new Date(status.last_run.at).toLocaleString()} by {status.last_run.started_by} · pages {status.last_run.pages} · fetched {status.last_run.fetched} · created {status.last_run.created} · updated {status.last_run.updated}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => runSync({ max_pages: 50 })} disabled={busy} className="btn-teal !py-1.5 inline-flex items-center gap-1.5" data-testid="fa-outlets-sync-btn">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Cloud size={13} />}
            {busy ? "Syncing…" : "Sync outlets now"}
          </button>
          <button onClick={() => runSync({ dry_run: true, max_pages: 1 })} disabled={busy} className="btn-secondary !py-1.5 inline-flex items-center gap-1.5" data-testid="fa-outlets-dryrun-btn">
            <RefreshCw size={12} /> Dry-run preview
          </button>
          <button onClick={() => { if (window.confirm("Full re-pull from LastRequestId=0? Re-scans every outlet and refreshes all physio profiles.")) runSync({ reset: true, max_pages: 500 }); }} disabled={busy} className="text-[11.5px] text-rose-700 hover:underline ml-auto">
            Reset cursor &amp; re-pull all
          </button>
        </div>
        {error && <div className="text-[12px] text-rose-700 inline-flex items-center gap-1"><AlertCircle size={12} /> {error}</div>}
        {result && (
          <div className="bg-white border border-emerald-200 rounded p-3 text-[11.5px]" data-testid="fa-outlets-result">
            <div className="text-emerald-700 font-medium mb-1.5 inline-flex items-center gap-1"><CheckCircle2 size={12} /> {result.dry_run ? "Dry-run complete" : "Sync complete"}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-slate-700 font-mono">
              <div>pages: <strong>{result.pages}</strong></div>
              <div>fetched: <strong>{result.fetched}</strong></div>
              <div>created: <strong className="text-emerald-700">{result.created}</strong></div>
              <div>updated: <strong>{result.updated}</strong></div>
              <div>skipped: <strong>{result.skipped}</strong></div>
              <div>cursor: <strong>{result.from_cursor} → {result.to_cursor}</strong></div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-2">
      <div className="small-caps text-slate-500 text-[10px]">{label}</div>
      <div className={`serif-display text-lg numerals ${tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

// Point 63 — bulk deduct/correct excess points across many physios (preview → apply).
function BulkPointCorrectionCard() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function run(dryRun, chosenFile) {
    const f = chosenFile || file;
    if (!f) return;
    setBusy(true); setError("");
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/admin/imports/points-correction?dry_run=${dryRun}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setApplied(dryRun ? null : data);
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  }

  function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setApplied(null); setPreview(null);
    run(true, f);
    if (inputRef.current) inputRef.current.value = "";
  }

  function apply() {
    if (!preview || !file || preview.will_change === 0) return;
    if (!window.confirm(`Apply ${fmt(preview.will_change)} point correction(s)?\n\nThis will DEDUCT a total of ${fmt(preview.total_deduct)} points (and add ${fmt(preview.total_add)}) across ${fmt(preview.matched)} matched physios.\n\nThis cannot be undone.`)) return;
    run(false);
  }

  function downloadTemplate() {
    const blob = new Blob(["ERP Outlet Id,Final Points,Deduct Points\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "points-correction-template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  const items = preview?.items || [];

  return (
    <Card
      title="Bulk Point Correction — Deduct Excess (Point 63)"
      subtitle="Upload your list to correct/deduct excess points across many physios at once. Identify each physio by ERP Outlet Id (Contact/mobile also accepted). Use EITHER a 'Final Points' column (Active is SET to that value) OR a 'Deduct Points' column (that amount is removed). Balances are clamped to 0. You'll ALWAYS see a preview first — nothing changes until you click Apply. Every change is logged as an auditable Correction."
    >
      <div className="bg-amber-50/40 border border-amber-200 rounded-md p-4 space-y-3" data-testid="bulk-correction-card">
        <div className="flex flex-wrap items-center gap-2">
          <span className="small-caps text-slate-400">Expected columns:</span>
          {["ERP Outlet Id", "Final Points", "Deduct Points"].map((c) => (
            <code key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600 font-mono">{c}</code>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="file" accept=".xlsx,.xls,.csv" ref={inputRef} onChange={onPick} className="hidden" data-testid="bpc-input" />
          <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="bpc-choose">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? "Reading…" : "Choose file (.xlsx / .csv)"}
          </button>
          <button onClick={downloadTemplate} className="text-[12px] text-teal-700 hover:underline inline-flex items-center gap-1" data-testid="bpc-template">
            <Download size={13} /> Download template
          </button>
          {file && <span className="text-[11.5px] text-slate-500">{file.name}</span>}
        </div>

        {error && <div className="tag tag-rose inline-flex items-start gap-2"><AlertCircle size={13} className="mt-0.5" /> <span data-testid="bpc-error">{error}</span></div>}

        {preview && (
          <div className="bg-white border border-slate-200 rounded-md p-3 space-y-3" data-testid="bpc-preview">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Matched physios" value={fmt(preview.matched)} />
              <Stat label={applied ? "Applied" : "Will change"} value={fmt(applied ? preview.applied : preview.will_change)} tone="amber" />
              <Stat label="Total to deduct" value={fmt(preview.total_deduct)} tone="amber" />
              <Stat label="Not found" value={fmt(preview.not_found)} />
            </div>
            {preview.clamped_count > 0 && (
              <div className="text-[11.5px] text-amber-800"><Tag tone="amber">Clamped to 0</Tag> {fmt(preview.clamped_count)} physio(s) would go below zero — set to 0 instead.</div>
            )}
            {preview.not_found > 0 && (
              <div className="text-[11.5px] text-rose-700"><Tag tone="rose">Not found</Tag> {(preview.not_found_sample || []).join(", ")}{preview.not_found > (preview.not_found_sample || []).length ? " …" : ""}</div>
            )}

            <div className="overflow-auto max-h-80 rounded border border-slate-100">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">ERP Outlet Id</th>
                    <th className="text-left font-medium px-3 py-2">Physio</th>
                    <th className="text-right font-medium px-3 py-2">Current</th>
                    <th className="text-right font-medium px-3 py-2">New</th>
                    <th className="text-right font-medium px-3 py-2">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it.physio_id} className={`border-t border-slate-100 ${it.delta !== 0 ? "" : "opacity-50"}`} data-testid={`bpc-row-${i}`}>
                      <td className="px-3 py-1.5 font-mono text-slate-600">{it.erp_outlet_id || it.mobile || "—"}</td>
                      <td className="px-3 py-1.5">{it.name}{it.clamped && <span className="ml-1"><Tag tone="amber">clamped</Tag></span>}</td>
                      <td className="px-3 py-1.5 text-right text-slate-700">{fmt(it.current)}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-slate-900">{fmt(it.target)}</td>
                      <td className={`px-3 py-1.5 text-right font-semibold ${it.delta < 0 ? "text-rose-600" : it.delta > 0 ? "text-emerald-600" : "text-slate-400"}`}>{it.delta > 0 ? "+" : ""}{fmt(it.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {applied ? (
              <div className="text-emerald-700 font-medium text-[12.5px] inline-flex items-center gap-1.5" data-testid="bpc-applied">
                <CheckCircle2 size={14} /> Applied {fmt(preview.applied)} correction(s) — {fmt(preview.total_deduct)} points deducted. Done.
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button onClick={apply} disabled={busy || preview.will_change === 0} className="bg-amber-600 hover:bg-amber-700 text-white text-[12.5px] px-3 py-1.5 rounded-md inline-flex items-center gap-2 disabled:opacity-50" data-testid="bpc-apply">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  {busy ? "Applying…" : `Apply ${fmt(preview.will_change)} correction(s)`}
                </button>
                <span className="text-[11.5px] text-slate-500">Preview only — review the table above before applying.</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// Point 64 — bulk-upload manual (offline) redemptions (preview → apply).
function ManualRedemptionCard() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function run(dryRun, chosenFile) {
    const f = chosenFile || file;
    if (!f) return;
    setBusy(true); setError("");
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/admin/imports/manual-redemptions?dry_run=${dryRun}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setApplied(dryRun ? null : data);
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  }

  function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setApplied(null); setPreview(null);
    run(true, f);
    if (inputRef.current) inputRef.current.value = "";
  }

  function apply() {
    if (!preview || !file || preview.will_create === 0) return;
    if (!window.confirm(`Create ${fmt(preview.will_create)} manual redemption(s)?\n\nThis will DEDUCT ${fmt(preview.total_deduct)} points from the physios' Active wallets and record the redemptions as Delivered. You can Revert individual ones later in Redemption Tracking.`)) return;
    run(false);
  }

  function downloadTemplate() {
    const blob = new Blob(["Physio ID,Redeemed Points,Product,Redeem Date\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "manual-redemptions-template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  const items = preview?.items || [];

  return (
    <Card
      title="Manual Redemption Upload (Point 64)"
      subtitle="For redemptions done offline. Map each to a physio by ERP Outlet Id (Contact/mobile also accepted). Columns: Physio ID, Redeemed Points, Product, Redeem Date. Each row is recorded as a completed (Delivered) redemption — visible in Customer Search, Redemption Tracking and the customer's history — and the points are deducted from their Active balance (clamped to available, never negative). You'll preview first; re-uploading skips rows already added."
    >
      <div className="bg-teal-50/40 border border-teal-200 rounded-md p-4 space-y-3" data-testid="manual-redemption-card">
        <div className="flex flex-wrap items-center gap-2">
          <span className="small-caps text-slate-400">Expected columns:</span>
          {["Physio ID", "Redeemed Points", "Product", "Redeem Date"].map((c) => (
            <code key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600 font-mono">{c}</code>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="file" accept=".xlsx,.xls,.csv" ref={inputRef} onChange={onPick} className="hidden" data-testid="mru-input" />
          <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="mru-choose">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? "Reading…" : "Choose file (.xlsx / .csv)"}
          </button>
          <button onClick={downloadTemplate} className="text-[12px] text-teal-700 hover:underline inline-flex items-center gap-1" data-testid="mru-template">
            <Download size={13} /> Download template
          </button>
          {file && <span className="text-[11.5px] text-slate-500">{file.name}</span>}
        </div>

        {error && <div className="tag tag-rose inline-flex items-start gap-2"><AlertCircle size={13} className="mt-0.5" /> <span data-testid="mru-error">{error}</span></div>}

        {preview && (
          <div className="bg-white border border-slate-200 rounded-md p-3 space-y-3" data-testid="mru-preview">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Matched rows" value={fmt(preview.matched)} />
              <Stat label={applied ? "Created" : "Will create"} value={fmt(applied ? preview.applied : preview.will_create)} tone="emerald" />
              <Stat label="Points to deduct" value={fmt(preview.total_deduct)} tone="amber" />
              <Stat label="Not found" value={fmt(preview.not_found)} />
            </div>
            {preview.duplicates > 0 && (
              <div className="text-[11.5px] text-slate-600"><Tag tone="slate">Already uploaded</Tag> {fmt(preview.duplicates)} row(s) match a redemption added earlier — they'll be skipped.</div>
            )}
            {preview.clamped_count > 0 && (
              <div className="text-[11.5px] text-amber-800"><Tag tone="amber">Clamped</Tag> {fmt(preview.clamped_count)} physio(s) had fewer Active points than redeemed — deduction capped at their balance.</div>
            )}
            {preview.not_found > 0 && (
              <div className="text-[11.5px] text-rose-700"><Tag tone="rose">Not found</Tag> {(preview.not_found_sample || []).join(", ")}{preview.not_found > (preview.not_found_sample || []).length ? " …" : ""}</div>
            )}

            <div className="overflow-auto max-h-80 rounded border border-slate-100">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">ERP Outlet Id</th>
                    <th className="text-left font-medium px-3 py-2">Physio</th>
                    <th className="text-left font-medium px-3 py-2">Product</th>
                    <th className="text-left font-medium px-3 py-2">Date</th>
                    <th className="text-right font-medium px-3 py-2">Points</th>
                    <th className="text-right font-medium px-3 py-2">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${it.duplicate ? "opacity-50" : ""}`} data-testid={`mru-row-${i}`}>
                      <td className="px-3 py-1.5 font-mono text-slate-600">{it.erp_outlet_id || it.mobile || "—"}</td>
                      <td className="px-3 py-1.5">{it.name}{it.duplicate && <span className="ml-1"><Tag tone="slate">dup</Tag></span>}{it.clamped && !it.duplicate && <span className="ml-1"><Tag tone="amber">clamped</Tag></span>}</td>
                      <td className="px-3 py-1.5 text-slate-700">{it.product}</td>
                      <td className="px-3 py-1.5 text-slate-500">{it.date}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-rose-600">-{fmt(it.deduct)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-700">{fmt(it.current)} → <strong className="text-slate-900">{fmt(it.new_active)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {applied ? (
              <div className="text-emerald-700 font-medium text-[12.5px] inline-flex items-center gap-1.5" data-testid="mru-applied">
                <CheckCircle2 size={14} /> Created {fmt(preview.applied)} redemption(s) — {fmt(preview.total_deduct)} points deducted. Done.
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button onClick={apply} disabled={busy || preview.will_create === 0} className="btn-teal !py-1.5 inline-flex items-center gap-2 disabled:opacity-50" data-testid="mru-apply">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  {busy ? "Creating…" : `Create ${fmt(preview.will_create)} redemption(s)`}
                </button>
                <span className="text-[11.5px] text-slate-500">Preview only — review the table above before creating.</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// Point 66 — bulk-upload referral points (preview → apply).
function ReferralPointsCard() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function run(dryRun, chosenFile) {
    const f = chosenFile || file;
    if (!f) return;
    setBusy(true); setError("");
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/admin/imports/referral-points?dry_run=${dryRun}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setApplied(dryRun ? null : data);
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  }

  function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setApplied(null); setPreview(null);
    run(true, f);
    if (inputRef.current) inputRef.current.value = "";
  }

  function apply() {
    if (!preview || !file || preview.will_credit === 0) return;
    if (!window.confirm(`Credit referral points to ${fmt(preview.will_credit)} physio(s)?\n\nThis will ADD a total of ${fmt(preview.total_credit)} points to their Active balance (and Annual Points), recorded as Referral Bonus.`)) return;
    run(false);
  }

  function downloadTemplate() {
    const blob = new Blob(["Physio ID,Referral Points,Remark,Referral Date\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "referral-points-template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  const items = preview?.items || [];

  return (
    <Card
      title="Referral Points Upload (Point 66)"
      subtitle="Credit physios' referral points in bulk. Map each by ERP Outlet Id (Contact/mobile also accepted). Columns: Physio ID, Referral Points (+ optional Remark, Referral Date). Points are added to the physio's Active balance (and Annual Points) and recorded as a Referral Bonus visible in their passbook. Preview first; re-uploading skips rows already credited."
    >
      <div className="bg-indigo-50/40 border border-indigo-200 rounded-md p-4 space-y-3" data-testid="referral-points-card">
        <div className="flex flex-wrap items-center gap-2">
          <span className="small-caps text-slate-400">Expected columns:</span>
          {["Physio ID", "Referral Points", "Remark", "Referral Date"].map((c) => (
            <code key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600 font-mono">{c}</code>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="file" accept=".xlsx,.xls,.csv" ref={inputRef} onChange={onPick} className="hidden" data-testid="rpu-input" />
          <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="rpu-choose">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? "Reading…" : "Choose file (.xlsx / .csv)"}
          </button>
          <button onClick={downloadTemplate} className="text-[12px] text-teal-700 hover:underline inline-flex items-center gap-1" data-testid="rpu-template">
            <Download size={13} /> Download template
          </button>
          {file && <span className="text-[11.5px] text-slate-500">{file.name}</span>}
        </div>

        {error && <div className="tag tag-rose inline-flex items-start gap-2"><AlertCircle size={13} className="mt-0.5" /> <span data-testid="rpu-error">{error}</span></div>}

        {preview && (
          <div className="bg-white border border-slate-200 rounded-md p-3 space-y-3" data-testid="rpu-preview">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Matched rows" value={fmt(preview.matched)} />
              <Stat label={applied ? "Credited" : "Will credit"} value={fmt(applied ? preview.applied : preview.will_credit)} tone="emerald" />
              <Stat label="Total points" value={fmt(preview.total_credit)} tone="emerald" />
              <Stat label="Not found" value={fmt(preview.not_found)} />
            </div>
            {preview.duplicates > 0 && (
              <div className="text-[11.5px] text-slate-600"><Tag tone="slate">Already credited</Tag> {fmt(preview.duplicates)} row(s) match a referral added earlier — they'll be skipped.</div>
            )}
            {preview.not_found > 0 && (
              <div className="text-[11.5px] text-rose-700"><Tag tone="rose">Not found</Tag> {(preview.not_found_sample || []).join(", ")}{preview.not_found > (preview.not_found_sample || []).length ? " …" : ""}</div>
            )}

            <div className="overflow-auto max-h-80 rounded border border-slate-100">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">ERP Outlet Id</th>
                    <th className="text-left font-medium px-3 py-2">Physio</th>
                    <th className="text-left font-medium px-3 py-2">Remark</th>
                    <th className="text-right font-medium px-3 py-2">Referral Pts</th>
                    <th className="text-right font-medium px-3 py-2">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${it.duplicate ? "opacity-50" : ""}`} data-testid={`rpu-row-${i}`}>
                      <td className="px-3 py-1.5 font-mono text-slate-600">{it.erp_outlet_id || it.mobile || "—"}</td>
                      <td className="px-3 py-1.5">{it.name}{it.duplicate && <span className="ml-1"><Tag tone="slate">dup</Tag></span>}</td>
                      <td className="px-3 py-1.5 text-slate-500">{it.remark || "—"}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-emerald-600">+{fmt(it.points)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-700">{fmt(it.current)} → <strong className="text-slate-900">{fmt(it.new_active)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {applied ? (
              <div className="text-emerald-700 font-medium text-[12.5px] inline-flex items-center gap-1.5" data-testid="rpu-applied">
                <CheckCircle2 size={14} /> Credited {fmt(preview.applied)} physio(s) — {fmt(preview.total_credit)} referral points added. Done.
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button onClick={apply} disabled={busy || preview.will_credit === 0} className="btn-teal !py-1.5 inline-flex items-center gap-2 disabled:opacity-50" data-testid="rpu-apply">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  {busy ? "Crediting…" : `Credit ${fmt(preview.will_credit)} physio(s)`}
                </button>
                <span className="text-[11.5px] text-slate-500">Preview only — review the table above before crediting.</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function AdminImports() {
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [resetError, setResetError] = useState("");
  const [platBusy, setPlatBusy] = useState(false);
  const [platResult, setPlatResult] = useState(null);
  const [platError, setPlatError] = useState("");
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [purgeError, setPurgeError] = useState("");
  const [erplessBusy, setErplessBusy] = useState(false);
  const [erplessResult, setErplessResult] = useState(null);
  const [erplessError, setErplessError] = useState("");

  async function purgeErpless() {
    const phrase = window.prompt(
      "⚠️ REMOVE PHYSIOS WITHOUT ERP ID\n\nThis permanently deletes every physio that has NO ERP Outlet ID (test/demo records) — along with their wallets, transactions, redemptions, milestone unlocks, referrals and onboarding sessions.\n\nReal physios always have an ERP ID, so only test data is removed. It also fills any blank ShopType on your Field Assist physios.\n\nType REMOVE to confirm:"
    );
    if (phrase !== "REMOVE") {
      if (phrase !== null) alert("Cancelled — type REMOVE exactly to confirm.");
      return;
    }
    setErplessBusy(true); setErplessError(""); setErplessResult(null);
    try {
      const { data } = await api.post("/admin/purge-erpless?confirm=REMOVE");
      setErplessResult(data);
    } catch (err) {
      setErplessError(formatError(err));
    } finally { setErplessBusy(false); }
  }

  async function purgeNonGmp() {
    const phrase = window.prompt(
      "⚠️ REMOVE TEST / NON-GMP DATA\n\nThis permanently deletes every physio whose ShopType is NOT 'Physiotherapist GMP' OR that has no ERP Outlet ID (test data) — along with their wallets, transactions, redemptions, milestone unlocks, referrals and onboarding sessions.\n\nKEPT: only Physiotherapist GMP physios that have a valid ERP Outlet ID.\n\nType PURGE to confirm:"
    );
    if (phrase !== "PURGE") {
      if (phrase !== null) alert("Cancelled — type PURGE exactly to confirm.");
      return;
    }
    setPurgeBusy(true); setPurgeError(""); setPurgeResult(null);
    try {
      const { data } = await api.post("/admin/purge-non-gmp?confirm=PURGE");
      setPurgeResult(data);
    } catch (err) {
      setPurgeError(formatError(err));
    } finally { setPurgeBusy(false); }
  }

  async function resetPlatform() {
    const phrase = window.prompt(
      "⚠️ RESET PLATFORM DATA\n\nThis removes ALL physios (name, city, state, mobile — everything), wallets, points, transactions, redemptions, referrals, campaigns, grievances, onboarding sessions, events/alerts AND the manager/DSE login users. It also resets the Field Assist sync cursors so the next sync re-pulls from scratch.\n\nKEPT: Milestones, Reward Catalog, Programs, Configuration, and your Super Admin + HO Admin logins.\n\nAfter reset, run the Outlets + Stock & Sales sync (or wait for the 6 AM job) to repopulate from Field Assist.\n\nType RESET to confirm:"
    );
    if (phrase !== "RESET") {
      if (phrase !== null) alert("Cancelled — type RESET exactly to confirm.");
      return;
    }
    setPlatBusy(true); setPlatError(""); setPlatResult(null);
    try {
      const { data } = await api.post("/admin/reset-platform-data?confirm=RESET");
      setPlatResult(data);
    } catch (err) {
      setPlatError(formatError(err));
    } finally { setPlatBusy(false); }
  }

  async function factoryReset() {
    const phrase = window.prompt("⚠️ This will WIPE all physios, wallets, transactions, rewards, redemptions, campaigns, sessions, products and audit data.\n\nTenants, admin/DSE users, programs and specialties will be PRESERVED.\n\nType WIPE-CBC-DATA to confirm:");
    if (phrase !== "WIPE-CBC-DATA") {
      if (phrase !== null) alert("Cancelled — confirmation phrase did not match.");
      return;
    }
    setResetBusy(true); setResetError(""); setResetResult(null);
    try {
      const { data } = await api.post("/admin/factory-reset", { confirm: "WIPE-CBC-DATA" });
      setResetResult(data);
    } catch (err) {
      setResetError(formatError(err));
    } finally { setResetBusy(false); }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Administration"
        title="Data Imports"
        subtitle="Bulk-load the CBC programme members, balance points and certification status from the source Excel sheets. All three imports are idempotent — re-running an import refreshes existing rows keyed on Outlet ERP Id."
        testId="admin-imports-page"
      />
      <div className="space-y-4">
        {SECTIONS.map((s) => <ImportCard key={s.id} section={s} />)}
      </div>

      <BulkPointCorrectionCard />

      <ManualRedemptionCard />

      <ReferralPointsCard />

      <FieldAssistOutletsSyncCard />

      <FieldAssistSyncCard />

      <Card title="Remove Physios Without ERP ID" subtitle="Delete test/demo physios that have no ERP Outlet ID, and fill any blank ShopType on your Field Assist physios. Real physios always carry an ERP ID, so only test data is removed." className="border-rose-200">
        <div className="bg-rose-50/40 border border-rose-200 rounded-md p-4 space-y-3" data-testid="purge-erpless-card">
          <div className="text-[12px] text-slate-700">
            Removes every physio with <strong>no ERP Outlet ID</strong> (the leftover test records) plus their wallets, transactions, redemptions, milestone unlocks, referrals and onboarding sessions. It first <strong>backfills the ShopType</strong> (<em>Physiotherapist GMP</em>) on your Field Assist physios so the Transaction &amp; Registration reports stop showing a blank ShopType.
          </div>
          <button onClick={purgeErpless} disabled={erplessBusy} className="bg-rose-600 hover:bg-rose-700 text-white text-[12.5px] px-3 py-1.5 rounded-md inline-flex items-center gap-2 disabled:opacity-50" data-testid="purge-erpless-btn">
            {erplessBusy ? <Loader2 size={14} className="animate-spin" /> : <AlertCircle size={14} />}
            {erplessBusy ? "Removing…" : "Remove physios without ERP ID"}
          </button>
          {erplessError && (
            <div className="text-[12px] text-rose-700 inline-flex items-center gap-1 pt-1" data-testid="purge-erpless-error"><AlertCircle size={12} /> {erplessError}</div>
          )}
          {erplessResult && (
            <div className="bg-white border border-emerald-200 rounded p-3 text-[11.5px]" data-testid="purge-erpless-result">
              <div className="text-emerald-700 font-medium mb-1.5 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Done — kept {fmt(erplessResult.kept_physios || 0)} physios with ERP ID · fixed {fmt(erplessResult.fixed_shoptype || 0)} blank ShopType</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-slate-700">
                {Object.entries(erplessResult.removed || {}).map(([col, n]) => (
                  <div key={col} className="font-mono">{col}: <strong>{typeof n === "number" ? fmt(n) : String(n)}</strong></div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card title="Remove Test / Non-GMP Data" subtitle="Keep ONLY Physiotherapist GMP physios with a valid ERP Outlet ID. Deletes test records (no ERP ID) and any wrong-ShopType physios + their points/transactions." className="border-rose-200">
        <div className="bg-rose-50/40 border border-rose-200 rounded-md p-4 space-y-3" data-testid="purge-gmp-card">
          <div className="text-[12px] text-slate-700">
            Use this to clean the panel after a sync — it removes physios whose <strong>ShopType ≠ Physiotherapist GMP</strong> or that have <strong>no ERP Outlet ID</strong> (test data), cascading their wallets, transactions, redemptions, milestone unlocks, referrals and onboarding sessions.
          </div>
          <button onClick={purgeNonGmp} disabled={purgeBusy} className="bg-rose-600 hover:bg-rose-700 text-white text-[12.5px] px-3 py-1.5 rounded-md inline-flex items-center gap-2 disabled:opacity-50" data-testid="purge-gmp-btn">
            {purgeBusy ? <Loader2 size={14} className="animate-spin" /> : <AlertCircle size={14} />}
            {purgeBusy ? "Removing…" : "Remove test / non-GMP data"}
          </button>
          {purgeError && (
            <div className="text-[12px] text-rose-700 inline-flex items-center gap-1 pt-1" data-testid="purge-gmp-error"><AlertCircle size={12} /> {purgeError}</div>
          )}
          {purgeResult && (
            <div className="bg-white border border-emerald-200 rounded p-3 text-[11.5px]" data-testid="purge-gmp-result">
              <div className="text-emerald-700 font-medium mb-1.5 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Done — kept {fmt(purgeResult.kept_gmp_physios || 0)} GMP physios</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-slate-700">
                {Object.entries(purgeResult.removed || {}).map(([col, n]) => (
                  <div key={col} className="font-mono">{col}: <strong>{typeof n === "number" ? fmt(n) : String(n)}</strong></div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card title="Reset Platform Data" subtitle="Clear all member/transaction data so the platform repopulates from the Field Assist APIs — KEEPS your Milestones, Reward Catalog, Programs, Config and admin logins." className="border-amber-200">
        <div className="bg-amber-50/50 border border-amber-200 rounded-md p-4 space-y-3" data-testid="reset-platform-card">
          <div className="grid sm:grid-cols-2 gap-3 text-[12px]">
            <div className="bg-white border border-emerald-200 rounded p-3">
              <div className="small-caps text-emerald-700 mb-1">✓ Kept</div>
              <ul className="list-disc list-inside text-slate-700 space-y-0.5">
                <li>Milestones &amp; Slabs</li>
                <li>Reward Catalog + Product Catalog</li>
                <li>Programs &amp; Configuration</li>
                <li>Super Admin + HO Admin logins</li>
              </ul>
            </div>
            <div className="bg-white border border-rose-200 rounded p-3">
              <div className="small-caps text-rose-700 mb-1">✕ Removed</div>
              <ul className="list-disc list-inside text-slate-700 space-y-0.5">
                <li>All Physios (name, city, state, mobile…)</li>
                <li>Wallets, Points &amp; Transactions</li>
                <li>Redemptions, Referrals, Campaigns, Grievances</li>
                <li>Manager + DSE users · Field Assist sync cursors</li>
              </ul>
            </div>
          </div>
          <div className="text-[11.5px] text-amber-900">
            After reset, tap <strong>Sync outlets now</strong> then <strong>Sync transactions</strong> above (or wait for the 6 AM job) — physios &amp; orders repopulate from Field Assist, and real DSEs are recreated automatically.
          </div>
          <button onClick={resetPlatform} disabled={platBusy} className="bg-amber-600 hover:bg-amber-700 text-white text-[12.5px] px-3 py-1.5 rounded-md inline-flex items-center gap-2 disabled:opacity-50" data-testid="reset-platform-btn">
            {platBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {platBusy ? "Resetting…" : "Reset Platform Data (keep Milestones + Rewards)"}
          </button>
          {platError && (
            <div className="text-[12px] text-rose-700 inline-flex items-center gap-1 pt-1" data-testid="reset-platform-error"><AlertCircle size={12} /> {platError}</div>
          )}
          {platResult && (
            <div className="bg-white border border-emerald-200 rounded p-3 text-[11.5px]" data-testid="reset-platform-result">
              <div className="text-emerald-700 font-medium mb-1.5 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Reset complete</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-slate-700">
                {Object.entries(platResult.deleted || {}).map(([col, n]) => (
                  <div key={col} className="font-mono">{col}: <strong>{typeof n === "number" ? fmt(n) : String(n)}</strong></div>
                ))}
              </div>
              <div className="mt-2 text-slate-500">Kept — admins: {platResult.kept?.admins}, programs: {platResult.kept?.programs}, milestones: {platResult.kept?.milestone_slabs}, rewards: {platResult.kept?.rewards}</div>
            </div>
          )}
        </div>
      </Card>

      <Card title="Danger Zone" subtitle="Wipe demo/test data and start fresh — preserves tenant, admin users, programs and specialty config." className="border-rose-200">
        <div className="bg-rose-50/40 border border-rose-200 rounded-md p-4 space-y-2">
          <div className="text-[12.5px] text-rose-900">
            <strong>Factory Reset</strong> deletes every physio, wallet, transaction, reward, redemption, campaign, session, product PDF, audit record and onboarding session. Super-admin only. Cannot be undone.
          </div>
          <button onClick={factoryReset} disabled={resetBusy} className="bg-rose-600 hover:bg-rose-700 text-white text-[12.5px] px-3 py-1.5 rounded-md inline-flex items-center gap-2 disabled:opacity-50" data-testid="factory-reset-btn">
            {resetBusy ? <Loader2 size={14} className="animate-spin" /> : <AlertCircle size={14} />}
            {resetBusy ? "Wiping…" : "Factory Reset (wipe all data)"}
          </button>
          {resetError && (
            <div className="text-[12px] text-rose-700 inline-flex items-center gap-1 pt-1" data-testid="factory-reset-error"><AlertCircle size={12} /> {resetError}</div>
          )}
          {resetResult && (
            <div className="bg-white border border-emerald-200 rounded p-3 text-[11.5px]" data-testid="factory-reset-result">
              <div className="text-emerald-700 font-medium mb-1.5 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Wiped at {new Date(resetResult.at).toLocaleString()}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-slate-700">
                {Object.entries(resetResult.wiped).map(([col, n]) => (
                  <div key={col} className="font-mono">{col}: <strong>{n}</strong></div>
                ))}
              </div>
              <div className="mt-2 text-slate-500">Kept: {resetResult.kept.join(", ")}</div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
